import React, { useEffect, useState } from "react";
import { Save, Send, Loader2, Plus } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, errText } from "@/lib/devadminApi";

const STATUS_COLOR = { queued: "var(--amber)", sending: "var(--amber)", sent: "var(--text)", delivered: "var(--green)", read: "var(--green)", failed: "var(--red-2)" };

export function WhatsAppTemplates({ eventId }) {
  const [rows, setRows] = useState(null);
  const load = () => api.get(`/events/${eventId}/whatsapp-templates`).then((r) => setRows(r.data)).catch((e) => toast.error(errText(e)));
  useEffect(() => { setRows(null); load(); }, [eventId]); // eslint-disable-line
  const add = async () => {
    const key = window.prompt("Template key (snake_case, e.g. stall_reminder)");
    if (!key) return;
    try { await api.post(`/events/${eventId}/whatsapp-templates`, { template_key: key.trim(), name: key.replace(/_/g, " "), aisensy_campaign_name: `bni_md_${key.trim()}`, variables_schema: ["name"], sort_order: rows.length }); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  if (!rows) return <div className="mono-label" data-testid="wa-templates-loading">Loading templates…</div>;
  return (
    <div className="space-y-4" data-testid="wa-templates">
      <div className="flex items-center justify-between">
        <div className="mono-label" style={{ color: "var(--taupe)" }}>Each key maps to a Live API campaign in AiSensy. templateParams are sent in the order of the variable schema.</div>
        <button data-testid="wa-template-add" onClick={add} className="btn-ghost"><Plus size={14} /> Add template</button>
      </div>
      {rows.map((t) => <TemplateCard key={t.id} row={t} onChange={(u) => setRows(rows.map((x) => x.id === u.id ? u : x))} />)}
    </div>
  );
}

function TemplateCard({ row, onChange }) {
  const [form, setForm] = useState({ name: row.name, aisensy_campaign_name: row.aisensy_campaign_name, variables: row.variables_schema.join(", "), button_url_param: row.button_url_param || "" });
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);

  const save = async () => {
    const body = { name: form.name, aisensy_campaign_name: form.aisensy_campaign_name, variables_schema: form.variables.split(",").map((s) => s.trim()).filter(Boolean), button_url_param: form.button_url_param.trim() || null };
    setBusy("save");
    try { const { data } = await api.patch(`/whatsapp-templates/${row.id}`, body); onChange(data); toast.success(`${data.name} saved`); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };
  const toggle = async (v) => {
    try { const { data } = await api.patch(`/whatsapp-templates/${row.id}`, { active: v }); onChange(data); } catch (e) { toast.error(errText(e)); }
  };
  const testSend = async () => {
    if (!phone) return toast.error("Enter a phone number");
    setBusy("send"); setResult(null);
    try {
      const { data } = await api.post(`/whatsapp-templates/${row.id}/test-send`, { phone, variables: { name: "Test" } });
      let m = data;
      for (let i = 0; i < 12 && ["queued", "sending"].includes(m.status); i++) {
        await new Promise((r) => setTimeout(r, 1000));
        m = (await api.get(`/whatsapp-messages/${data.id}`)).data;
      }
      setResult(m);
      (m.status === "sent" ? toast.success : toast.error)(`${row.name}: ${m.status}${m.failure_reason ? ` · ${m.failure_reason}` : ""}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(""); }
  };

  return (
    <Panel className="p-6" data-testid={`wa-template-${row.template_key}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-serif text-xl">{row.name}</div>
          <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>key · {row.template_key}</div>
        </div>
        <label className="flex items-center gap-2 mono-label"><Switch data-testid={`wa-template-active-${row.template_key}`} checked={row.active} onCheckedChange={toggle} /> {row.active ? "Active" : "Inactive"}</label>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div><MonoLabel>Display name</MonoLabel><input data-testid={`wa-template-name-${row.template_key}`} className="input-x mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><MonoLabel>AiSensy campaign name</MonoLabel><input data-testid={`wa-template-campaign-${row.template_key}`} className="input-x mt-2 !font-mono" value={form.aisensy_campaign_name} onChange={(e) => setForm({ ...form, aisensy_campaign_name: e.target.value })} /></div>
        <div><MonoLabel>Variables (in template order)</MonoLabel><input data-testid={`wa-template-vars-${row.template_key}`} className="input-x mt-2 !font-mono" value={form.variables} onChange={(e) => setForm({ ...form, variables: e.target.value })} /></div>
        <div><MonoLabel>URL button variable</MonoLabel><input data-testid={`wa-template-btn-${row.template_key}`} className="input-x mt-2 !font-mono" placeholder="blank if no button" value={form.button_url_param} onChange={(e) => setForm({ ...form, button_url_param: e.target.value })} /></div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        <button data-testid={`wa-template-save-${row.template_key}`} onClick={save} disabled={!!busy} className="btn-red">{busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <div className="flex items-stretch ml-auto">
          <input data-testid={`wa-template-phone-${row.template_key}`} className="input-x !w-44 !font-mono" placeholder="10-digit phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <button data-testid={`wa-template-send-${row.template_key}`} onClick={testSend} disabled={!!busy} className="btn-ghost border-l-0">{busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Test send</button>
        </div>
      </div>
      {result && (
        <div data-testid={`wa-template-result-${row.template_key}`} className="mt-3 p-3 hairline text-sm">
          <span className="mono-label" style={{ color: STATUS_COLOR[result.status] }}>{result.status.toUpperCase()}</span>
          <span className="mono-label ml-3" style={{ color: "var(--taupe)" }}>attempt {result.attempt_count}{result.aisensy_message_id ? ` · id ${result.aisensy_message_id}` : ""}</span>
          {result.failure_reason && <div className="mt-1" style={{ color: "var(--text)" }}>{result.failure_reason}</div>}
        </div>
      )}
    </Panel>
  );
}
